import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaPlus, FaTrash, FaTimes, FaDownload } from 'react-icons/fa';
import { uploadFile, deleteFile, getFile } from '../../api/files';
import { useBaseComponentFullscreen } from './BaseComponent';

export default function GalleryComponent({ component, onUpdate, refreshSidebar }) {
  const isFullscreen = useBaseComponentFullscreen();
  const data = component.data || { images: [] };
  const [isUploading, setIsUploading] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Fetch images as blobs and create object URLs
  useEffect(() => {
    const fetchImages = async () => {
      const urls = {};
      for (const image of data.images) {
        try {
          const blob = await getFile(image.path, localStorage.getItem('access_token'));
          urls[image.path] = URL.createObjectURL(blob);
        } catch (error) {
          console.error(`Failed to load image ${image.path}:`, error);
        }
      }
      setImageUrls(urls);
    };
    fetchImages();

    return () => {
      Object.values(imageUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [data.images]);

  // Handle drag-and-drop events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (!file || !['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      alert('Please drop a valid image file (JPEG, PNG, GIF)');
      return;
    }

    await handleUploadFile(file);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await handleUploadFile(file);
  };

  const handleUploadFile = async (file) => {
    if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF)');
      return;
    }

    setIsUploading(true);
    try {
      const response = await uploadFile(component.idea_id, file, localStorage.getItem('access_token'));
      const newImages = [
        ...data.images,
        {
          path: response.file_path,
          name: file.name,
          size: file.size,
          uploaded_at: new Date().toISOString()
        }
      ];
      onUpdate({
        ...component,
        data: { images: newImages }
      });
      if (typeof refreshSidebar === 'function') {
        refreshSidebar();
      }
    } catch (error) {
      alert(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData.items;
    for (let item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          // Add a brief visual feedback for paste
          const originalBg = dropZoneRef.current?.style.backgroundColor;
          if (dropZoneRef.current) {
            dropZoneRef.current.style.backgroundColor = 'var(--bg-hover)';
            dropZoneRef.current.style.transition = 'background-color 0.3s';
          }
          
          await handleUploadFile(file);
          
          // Reset background after a delay
          setTimeout(() => {
            if (dropZoneRef.current) {
              dropZoneRef.current.style.backgroundColor = originalBg || '';
            }
          }, 500);
        }
        return;
      }
    }
  };

  const handleDelete = async (image) => {
    setDeleteConfirm(image);
  };

  const confirmDelete = async () => {
    const image = deleteConfirm;
    setDeleteConfirm(null);

    try {
      await deleteFile(image.path, localStorage.getItem('access_token'));
      const newImages = data.images.filter(img => img.path !== image.path);
      onUpdate({
        ...component,
        data: { images: newImages }
      });
      if (typeof refreshSidebar === 'function') {
        refreshSidebar();
      }
      if (imageUrls[image.path]) {
        URL.revokeObjectURL(imageUrls[image.path]);
        setImageUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[image.path];
          return newUrls;
        });
      }
      setSelectedImage(null);
    } catch (error) {
      alert(`Delete failed: ${error.message}`);
    }
  };

  const handleDownload = async (image) => {
    try {
      const blob = await getFile(image.path, localStorage.getItem('access_token'));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = image.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert(`Download failed: ${error.message}`);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getGridStyle = () => {
    const count = data.images.length;
    if (count === 1) return 'grid-cols-1';
    if (count === 2) return isFullscreen ? 'grid-cols-2' : 'grid-cols-2';
    if (count === 3 || count === 4) return isFullscreen ? 'grid-cols-3 grid-rows-2' : 'grid-cols-2 grid-rows-2';
    return isFullscreen ? 'grid-cols-4' : 'grid-cols-3';
  };

  return (
    <div 
      className="relative max-w-4xl mx-auto outline-none focus:outline-none focus:shadow-[0_0_0_2px_var(--secondary)] focus:shadow-opacity-30 rounded-lg transition-all duration-200" 
      tabIndex={0} 
      onPaste={handlePaste}
      onFocus={() => {}} // Make it focusable for paste events
      title="Click here and press Ctrl+V to paste images"
    >
      {/* Image Grid with Drag-and-Drop */}
      <div
        ref={dropZoneRef}
        className={`grid ${getGridStyle()} gap-4 ${isFullscreen ? 'max-h-[70vh]' : 'max-h-[400px]'} overflow-y-auto relative ${
          isDragging ? 'border-2 border-dashed border-[var(--primary)] bg-[var(--bg-hover)]/50' : ''
        }`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {data.images.length === 0 ? (
          <div className="text-[var(--text-secondary)] text-center col-span-full">
            <p className="mb-2">No images uploaded yet.</p>
            <p className="text-sm">
              Drag and drop an image here, use the button below, or{' '}
              <span className="font-medium text-[var(--secondary)]">paste an image (Ctrl+V)</span> anywhere in this area.
            </p>
          </div>
        ) : (
          data.images.map((image, index) => (
            <div
              key={index}
              className="cursor-pointer overflow-hidden"
              onClick={() => setSelectedImage(image)}
            >
              {imageUrls[image.path] ? (
                <img
                  src={imageUrls[image.path]}
                  alt={image.name}
                  className="w-full aspect-square object-cover rounded-lg hover:scale-105 transition-transform duration-200"
                />
              ) : (
                <div className="w-full aspect-square bg-[var(--bg-hover)] rounded-lg flex items-center justify-center text-[var(--text-secondary)]">
                  Loading...
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Upload Button */}
      <div className="mt-4">
        <label className="cursor-pointer p-2 bg-[var(--primary)] text-white rounded hover:opacity-90 flex items-center gap-2 justify-center w-full disabled:opacity-50">
          <FaPlus /> {isUploading ? 'Uploading...' : 'Upload an Image'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif"
            className="hidden"
            ref={fileInputRef}
            onChange={handleUpload}
            disabled={isUploading}
          />
        </label>
        {!isUploading && (
          <p className="text-xs text-[var(--text-secondary)] mt-2 text-center">
            You can also paste images directly with Ctrl+V
          </p>
        )}
        
        {isUploading && (
          <div className="mt-3 text-center text-[var(--text-secondary)] text-sm">
            <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--primary)] mr-2"></div>
            Uploading image...
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-[var(--bg-primary)] p-6 rounded-lg max-w-3xl w-full border border-[var(--bg-hover)] mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg text-[var(--text-primary)] truncate">{selectedImage.name}</h3>
              <button
                onClick={() => setSelectedImage(null)}
                className="p-2 text-[var(--text-secondary)] hover:text-[var(--primary)]"
              >
                <FaTimes />
              </button>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                {imageUrls[selectedImage.path] ? (
                  <img
                    src={imageUrls[selectedImage.path]}
                    alt={selectedImage.name}
                    className="w-full max-h-[500px] object-contain rounded"
                  />
                ) : (
                  <div className="w-full h-[500px] bg-[var(--bg-hover)] rounded flex items-center justify-center text-[var(--text-secondary)]">
                    Loading...
                  </div>
                )}
              </div>
              <div className="w-full md:w-64 flex flex-col gap-4">
                <div>
                  <p className="text-sm text-[var(--text-secondary)]">Name:</p>
                  <p className="text-[var(--text-primary)] truncate">{selectedImage.name}</p>
                </div>
                <div>
                  <p className="text-sm text-[var(--text-secondary)]">Uploaded:</p>
                  <p className="text-[var(--text-primary)]">{new Date(selectedImage.uploaded_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-[var(--text-secondary)]">File Size:</p>
                  <p className="text-[var(--text-primary)]">{formatFileSize(selectedImage.size)}</p>
                </div>
                <button
                  onClick={() => handleDownload(selectedImage)}
                  className="p-2 bg-[var(--primary)] text-white rounded hover:bg-[var(--primary-dark)] flex items-center gap-2 justify-center"
                >
                  <FaDownload /> Download Image
                </button>
                <button
                  onClick={() => handleDelete(selectedImage)}
                  className="p-2 bg-danger text-white rounded hover:bg-danger hover:scale-105 hover:shadow-lg transition-all duration-200 flex items-center gap-2 justify-center"
                >
                  <FaTrash /> Delete Image
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] animate-fade-in">
          <div className="bg-[var(--bg-primary)] p-6 rounded-lg max-w-sm w-full mx-4 shadow-xl border border-[var(--border-color)] animate-scale-in">
            <h3 className="text-lg font-medium mb-4 text-[var(--text-primary)]">Delete Image</h3>
            <p className="mb-6 text-[var(--text-secondary)]">
              Are you sure you want to delete this image? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 rounded-lg bg-[var(--bg-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-all duration-200 transform hover:scale-105"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-lg bg-danger hover:bg-danger hover:shadow-lg hover:brightness-110 text-white transition-all duration-200 transform hover:scale-105"
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}